import { useMemo, useState } from 'react'
import { ProducerDrawerProps } from './types'
import styles from './ProducerDrawer.module.scss'
import { EnergyType, Producer } from 'types/producer'
import { normalizeText } from 'utils/format'
import { useTranslation } from 'next-i18next/pages'
import { filterAndSortProducers, getProducerLocation } from 'utils/producers'
import { InputGroupKeys, SimulationFieldKeys } from 'store/customer/enums'
import { useGetPersonalizedOfferQuery, useGetProducersQuery } from 'store/api/bolt-api'
import { useStoreSelector } from 'hooks/store'
import useSelectInputGroup from 'hooks/useSelectInputGroup'
import { Drawer, Search } from '@boltenergy-be/design-system'
import { AddressFields } from 'components/inputs/address/types'
import ProducerCard from 'components/producer-card/ProducerCard'
import { getTotalElectricityVolumeForMeterType } from 'utils/usage'
import { useGetLanguage } from 'hooks/useGetLanguage'

const ProducerDrawer = ({ isOpen, onRequestClose, onSelectProducer }: ProducerDrawerProps) => {
  // Redux store
  const deliveryAddress = useSelectInputGroup(InputGroupKeys.DELIVERY_ADDRESS)
  const simulation = useSelectInputGroup(InputGroupKeys.SIMULATION)
  const { didSimulation } = useStoreSelector((store) => store.customer.flows.simulation)
  const { postalCode, townCode, townName } = deliveryAddress.address

  // RTK queries
  const { data: producers } = useGetProducersQuery()
  const { data: personalizedProducers } = useGetPersonalizedOfferQuery({
    address: {
      [AddressFields.POSTAL_CODE]: postalCode,
      [AddressFields.TOWN_CODE]: townCode,
      [AddressFields.TOWN_NAME]: townName
    },
    didSimulation,
    meterUsage: simulation.usage
  })

  // Local state
  const [searchQuery, setSearchQuery] = useState<string>('')

  // I18n
  const { t } = useTranslation(['producers', 'common'])
  const language = useGetLanguage()
  const uppercaseLanguage = useGetLanguage('uppercase')

  // Constants
  const { popular, nearest, recent } = personalizedProducers?.[language] ?? {}
  const popularProducer = popular ? producers?.[popular] : undefined
  const nearestProducer = nearest ? producers?.[nearest] : undefined
  const recentProducer = recent ? producers?.[recent] : undefined

  // Memoized constants
  // List of producers that have availability and are sorted
  const availableAndSortedProducers = useMemo(
    () =>
      producers
        ? filterAndSortProducers({
            capacity: getTotalElectricityVolumeForMeterType(
              simulation[SimulationFieldKeys.METER_TYPE],
              simulation[SimulationFieldKeys.USAGE]
            ),
            hideSoldOut: true,
            language,
            producers: Object.values(producers)
          })
        : [],
    [simulation, language, producers]
  )

  // List of producers that match the search query
  const searchedProducers = useMemo<Producer[]>(() => {
    if (!producers) return []

    const normalizedQuery = normalizeText(searchQuery)

    return availableAndSortedProducers.filter(
      (p) =>
        normalizeText(p?.name[language])?.includes(normalizedQuery) ||
        normalizeText(getProducerLocation(language, p?.location))?.includes(normalizedQuery) ||
        normalizeText(t(`common:energyType.${p.energyType || EnergyType.WIND}`))?.includes(normalizedQuery)
    )
  }, [availableAndSortedProducers, language, producers, searchQuery, t])

  // List of producers that are not personalized
  const filteredProducers = useMemo<Producer[]>(
    () =>
      personalizedProducers?.[language] && !searchQuery
        ? availableAndSortedProducers.filter((p) => !Object.values(personalizedProducers[language]).includes(p.slug))
        : searchedProducers,
    [availableAndSortedProducers, language, personalizedProducers, searchQuery, searchedProducers]
  )

  return (
    <Drawer {...{ isOpen, onRequestClose }} className={styles.drawer} title={t('drawer.title')} serifTitle>
      <p>{t('drawer.description')}</p>

      {searchedProducers && (
        <>
          <Search
            label={t('drawer.search')}
            hiddenLabel
            className={styles.search}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onClear={() => setSearchQuery('')}
            placeholder={t('drawer.search')}
            language={uppercaseLanguage}
          />

          <ul className={styles.producers}>
            {!searchQuery && (
              <>
                {popularProducer && (
                  <li>
                    <ProducerCard onButtonClick={() => onSelectProducer(popularProducer)} producer={popularProducer} />
                  </li>
                )}

                {nearestProducer && (
                  <li>
                    <ProducerCard onButtonClick={() => onSelectProducer(nearestProducer)} producer={nearestProducer} />
                  </li>
                )}

                {recentProducer && (
                  <li>
                    <ProducerCard onButtonClick={() => onSelectProducer(recentProducer)} producer={recentProducer} />
                  </li>
                )}
              </>
            )}

            {!!filteredProducers.length &&
              filteredProducers.map((producer, index) => (
                <li key={index}>
                  <ProducerCard onButtonClick={() => onSelectProducer(producer)} producer={producer} />
                </li>
              ))}

            {!searchedProducers.length && <li className={styles['no-results']}>{t('noResults', 'Geen resultaten')}</li>}
          </ul>
        </>
      )}
    </Drawer>
  )
}

export default ProducerDrawer
